import { Injectable } from '@angular/core';

import {
  NotificationStreamCommand,
  NotificationStreamCommandJoin,
  NotificationStreamCommandLoad,
  NotificationStreamCommandMarkAllSeen,
  NotificationStreamCommandMarkUnitRoomRead,
  NotificationStreamCommandSend,
} from '../classes/websocketNotificationCommands';

import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import {
  AppendNotification,
  MarkAllSeen,
  MarkUnitRoomSeen,
  UpdateNotifications,
} from '../../core/actions/notifications';
@Injectable()
export class NotificationStreamService {
  private socket$: WebSocket;
  // The unit that the current load command
  // was supplied with
  private unit_connection?: number = null;
  private hasLoadedNotifications: boolean = false;
  private _RETRY_TIMER: number = 12 * 1000;

  constructor(private store: Store<fromRoot.State>) {}

  public establishConnection() {
    if (this.socket$ == null) {
      const access_token = localStorage.getItem('id_token');
      if (!access_token) {
        return;
      }
      this.socket$ = new WebSocket(
        environment.notification_stream_websocket_url +
          '?access_token=' +
          access_token
      );
    }

    if (this.socket$.readyState === this.socket$.OPEN) {
      return;
    }

    this.socket$.onopen = event => {
      if (this.socket$ != null) {
        this.sendJoinNotificationStream();
        const selected_unit_id: number = +localStorage.getItem('unit_id');
        if (selected_unit_id && !this.hasLoadedNotifications) {
          this.unit_connection = selected_unit_id;
          this.sendLoadNotificationStream(selected_unit_id);
        }
      }
    };

    this.socket$.onclose = event => {
      if (event.code !== 1000) {
        // Code 1000 means we
        // initiated the disconnect programmatically
        this.retryConnection();
      }

      this.hasLoadedNotifications = false;
      this.unit_connection = null;
    };

    this.socket$.onerror = event => {
      console.log(event);
    };

    this.socket$.onmessage = event => {
      const message = event.data;
      // let chatCommand: ChatCommand = ObjectMapper.deserialize(new ChatCommand(""), message);
      const chatCommand: NotificationStreamCommand =
        typeof message != null ? JSON.parse(message) : null;

      if (chatCommand && chatCommand.command) {
        if (chatCommand.command === 'join') {
          const notificationStreamCommandJoin: NotificationStreamCommandJoin = JSON.parse(
            message
          );
          this.handleNotificationStreamCommandJoin(
            notificationStreamCommandJoin
          );
        } else if (chatCommand.command === 'load_recruiter_applicants') {
          const notificationStreamCommandLoad: NotificationStreamCommandLoad = JSON.parse(
            message
          );
          this.handleNotificationStreamCommandLoad(
            notificationStreamCommandLoad
          );
        } else if (chatCommand.command === 'send') {
          const notificationStreamCommandSend: NotificationStreamCommandSend = JSON.parse(
            message
          );
          this.handleNotificationStreamCommandSend(
            notificationStreamCommandSend
          );
        } else if (chatCommand.command === 'mark_unit_room_read_recruiter') {
          const notificationStreamCommandMarkUnitRoomAsRead: NotificationStreamCommandMarkUnitRoomRead = JSON.parse(
            message
          );
          this.handleNotificationStreamCommandMarkUnitRoomAsRead(
            notificationStreamCommandMarkUnitRoomAsRead
          );
        } else if (chatCommand.command === 'mark_all_seen_recruiter') {
          const notificationStreamCommandMarkAllSeen: NotificationStreamCommandMarkAllSeen = JSON.parse(
            message
          );
          this.handleNotificationStreamCommandMarkAllSeen(
            notificationStreamCommandMarkAllSeen
          );
        }
      }
    };
  }

  public disconnect() {
    if (this.socket$ != null) {
      this.hasLoadedNotifications = false;
      this.unit_connection = null;
      this.socket$.onclose = null;
      this.socket$.close();
      this.socket$ = null;
    }
  }

  public didChangeUnit() {
    this.hasLoadedNotifications = false;
    const selected_unit_id: number = +localStorage.getItem('unit_id');
    if (selected_unit_id) {
      this.sendLoadNotificationStream(selected_unit_id);
    }
  }

  private handleNotificationStreamCommandJoin(
    notificationStreamCommandJoin: NotificationStreamCommandJoin
  ) {
    if (!this.hasLoadedNotifications) {
      const unit_id: number = +localStorage.getItem('unit_id');
      if (!unit_id) {
        return;
      }

      this.sendLoadNotificationStream(unit_id);
    }
  }

  private handleNotificationStreamCommandLoad(
    notificationStreamCommandLoad: NotificationStreamCommandLoad
  ) {
    const newValue = {
      notifications: notificationStreamCommandLoad.notifications,
      unit: notificationStreamCommandLoad.unit,
      unread: notificationStreamCommandLoad.unread,
      unseen: notificationStreamCommandLoad.unseen,
    };

    this.store.dispatch(new UpdateNotifications(newValue));
  }

  private handleNotificationStreamCommandSend(
    notificationStreamCommandSend: NotificationStreamCommandSend
  ) {
    this.store.dispatch(
      new AppendNotification(notificationStreamCommandSend.notification)
    );
  }

  private handleNotificationStreamCommandMarkAllSeen(
    notificationStreamCommandMarkAllSeen: NotificationStreamCommandMarkAllSeen
  ) {
    this.store.dispatch(new MarkAllSeen());
  }

  private handleNotificationStreamCommandMarkUnitRoomAsRead(
    notificationStreamCommandMarkUnitRoomAsRead: NotificationStreamCommandMarkUnitRoomRead
  ) {
    this.store.dispatch(
      new MarkUnitRoomSeen(
        notificationStreamCommandMarkUnitRoomAsRead.unit_room
      )
    );
  }

  private socket_send(data: any): void {
    const stringified_object = JSON.stringify(data);
    this.socket$.send(stringified_object);
  }

  private sendJoinNotificationStream() {
    const commandJoin = new NotificationStreamCommandJoin();
    this.socket_send(commandJoin);
  }

  private sendLoadNotificationStream(selected_unit_id: number) {
    this.hasLoadedNotifications = true;
    const commandLoad = new NotificationStreamCommandLoad(selected_unit_id);
    this.socket_send(commandLoad);
  }

  public sendMarkAllAsSeen(selected_unit_id: number) {
    const commandMarkAllSeen = new NotificationStreamCommandMarkAllSeen(
      selected_unit_id
    );
    this.socket_send(commandMarkAllSeen);
  }

  public sendMarkUnitRoomAsRead(selected_unit_id: number, unit_room: number) {
    const commandMarkAllAsRead = new NotificationStreamCommandMarkUnitRoomRead(
      selected_unit_id,
      unit_room
    );
    this.socket_send(commandMarkAllAsRead);
  }

  private retryConnection() {
    const unit_id: number = +localStorage.getItem('unit_id');
    if (
      unit_id &&
      this.socket$ &&
      this.socket$.readyState !== this.socket$.OPEN
    ) {
      // Code 1000 means we
      // initiated the disconnect programmatically
      setTimeout(() => {
        this.socket$ = null;
        this.establishConnection();
      }, this._RETRY_TIMER);
    }
  }
}
